<template>
  <v-container fluid>
    <toolbar mode="application" :item="application">
      <v-spacer />
      <template v-if="normal">
        <!-- <print-analysis :payload="payload()" :methods="methods" :disabled="isEmpty(result)" /> -->
        <ax-button icon="mdi-export-variant" tooltip="Export" @clicked="onExport" :disabled="isEmpty(result)" />
        <v-chip close @click:close="dialog=true">{{ prediction.name }}</v-chip>
      </template>
    </toolbar>
    <v-card flat v-if="!dialog">
      <v-row>
        <v-col cols="6">
          <v-card flat tile>
            <v-toolbar flat tile>
              <v-badge inline :content="tWells">
                <v-toolbar-title>Map</v-toolbar-title>
              </v-badge>
              <v-spacer />
            </v-toolbar>
            <vue-plotly
              id="map"
              v-if="charts.map.data.length"
              class="full"
              :data="charts.map.data"
              :layout="charts.map.layout"
              :options="charts.map.options"
              @afterplot="charts.map.loaded=true"
              @click="onMapClick"
              @selected="onMapSelect"
              @deselect="onMapClear"
            />
          </v-card>
          <v-divider />
          <v-card flat tile>
            <v-toolbar flat tile>
              <v-badge inline :content="nWells">
                <v-toolbar-title>Filters</v-toolbar-title>
              </v-badge>
              <v-spacer />
              <ax-button icon="mdi-playlist-plus" tooltip="Group wells" @clicked="onCreateGroup" />
              <ax-button icon="mdi-content-copy" tooltip="Copy group" @clicked="onCopyGroup" :disabled="!selectedGroup" />
              <dlg-rename-group
                :old_name="isObject(selectedGroup) ? selectedGroup.name : null"
                :key="isObject(selectedGroup) ? selectedGroup.name : null"
                @submitted="onRenameGroup"
                :disabled="!selectedGroup"
              ></dlg-rename-group>
              <ax-button icon="mdi-content-save-outline" tooltip="Save Session" @clicked="onSaveSession" />
              <ax-button icon="mdi-close" tooltip="Reset filters" @clicked="onResetFilters" />
            </v-toolbar>
            <v-card-text v-if="groups.list.length">
              <v-chip-group v-model="groups.selected" column>
                <v-chip
                  v-for="group in groups.list"
                  :key="group.id"
                  :value="group.id"
                  close
                  active-class="primary"
                  @click:close="onDeleteGroup(group.id)"
                >
                  {{ group.name }} ({{ group.count }} wells)
                </v-chip>
              </v-chip-group>
            </v-card-text>
            <v-card-text class="text--secondary" v-else>You don't have any saved groups</v-card-text>
            <v-container class="py-0">
              <v-row>
                <v-col>
                  <v-toolbar flat color="transparent"><v-toolbar-title>Settings</v-toolbar-title></v-toolbar>
                  <v-autocomplete label="Target" outlined dense :items="targets.options" v-model="targets.selected" :menu-props="{ bottom: true, offsetY: true }" @change="fetchResults" />
                  <!-- <v-autocomplete label="Quality" outlined dense :items="quality.options" v-model="quality.selected" :menu-props="{ bottom: true, offsetY: true }" @change="fetchResults" /> -->
                  <v-autocomplete v-if="isSuper" label="Metric" outlined dense :items="metrics.options" v-model="metrics.selected" :menu-props="{ bottom: true, offsetY: true }" @change="fetchResults" />
                  <v-autocomplete v-if="isSuper" label="Baseline" outlined dense :items="baseline.options" v-model="baseline.selected" :menu-props="{ bottom: true, offsetY: true }" @change="fetchResults" />
                  <v-autocomplete label="Operator" outlined dense :items="operator.options" v-model="operator.selected" :menu-props="{ bottom: true, offsetY: true }" @change="fetchResults" />
                </v-col>
                <v-col>
                  <v-toolbar flat color="transparent"><v-toolbar-title>Nominals</v-toolbar-title></v-toolbar>
                  <template v-for="(filter, column, index) in filteredObjects">
                    <v-autocomplete
                      :key="`${column}_${index}`"
                      v-model="filter.selected"
                      :items="filter.list"
                      multiple
                      outlined
                      dense
                      :label="column"
                      clearable
                      @change="fetchResults"
                    ></v-autocomplete>
                  </template>
                </v-col>
                <v-col>
                  <v-toolbar flat color="transparent"><v-toolbar-title>Scalars</v-toolbar-title></v-toolbar>
                  <template v-for="(filter, column, index) in filteredNumbers">
                    <v-range-slider
                      :label="column"
                      class="mb-3"
                      :key="`${column}_${index}`"
                      v-model="filter.selected"
                      :min="filter.min"
                      :max="filter.max"
                      thumb-label
                      persistent-hint
                      :hint="`(Between ${numberFormat(filter.selected[0])} and ${numberFormat(filter.selected[1])})`"
                      @change="fetchResults"
                    >
                      <template v-slot:thumb-label="props">
                        {{ props.value | roundedFormat }}
                      </template>
                    </v-range-slider>
                  </template>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card flat tile>
            <v-toolbar flat tile>
              <v-toolbar-title>Aggregates (all targets)</v-toolbar-title>
              <v-spacer />
            </v-toolbar>
            <v-data-table 
              class="text-no-wrap"
              :items="statistics.items" 
              :headers="statistics.headers" 
              v-model="statistics.selected" 
              item-key="target"
              dense hide-default-footer disable-sort
            >
              <template v-slot:[`item.arps1`]="{ item }">{{ item.arps1 | percentFormat }}</template>
              <template v-slot:[`item.multi1`]="{ item }">{{ item.multi1 | percentFormat }}</template>
              <template v-slot:[`item.multi2`]="{ item }">{{ item.multi2 | percentFormat }}</template>
              <template v-slot:[`item.multi3a`]="{ item }">{{ item.multi3a | percentFormat }}</template>
              <template v-slot:[`item.advanced1`]="{ item }">{{ item.advanced1 | percentFormat }}</template>
              <template v-slot:[`item.advanced2`]="{ item }">{{ item.advanced2 | percentFormat }}</template>
              <template v-slot:[`item.advanced3a`]="{ item }">{{ item.advanced3a | percentFormat }}</template>
            </v-data-table>
          </v-card>
          <v-divider />
          <v-card flat tile>
            <v-toolbar flat tile>
              <v-toolbar-title>{{ selectedWell || 'Timeline' }} ({{ targets.selected }})</v-toolbar-title>
              <v-spacer />
              <ax-button icon="mdi-close" tooltip="Close well" v-if="selectedWell" @clicked="selectedWell=null" />
            </v-toolbar>
            <vue-plotly
              :data="charts.timeline.data"
              :layout="charts.timeline.layout"
              :options="charts.timeline.options"
            />
          </v-card>
          <v-divider />
          <v-card flat tile>
            <v-toolbar flat tile>
              <v-toolbar-title>Individual Wells ({{ targets.selected }})</v-toolbar-title>
              <v-spacer />
              <ax-button icon="mdi-playlist-edit" tooltip="Remove well" @clicked="onRemoveWell" v-if="!!selectedGroup" />
            </v-toolbar>
            <v-data-table 
              class="text-no-wrap hover"
              :items="individuals.items" 
              :headers="individuals.headers" 
              dense item-key="api" :search="individuals.search"
              show-select @click:row="onRowClick"
              v-model="individuals.selected" 
            >
              <template v-slot:[`item.arps1`]="{ item }">{{ item.arps1 | percentFormat }}</template>
              <template v-slot:[`item.multi1`]="{ item }">{{ item.multi1 | percentFormat }}</template>
              <template v-slot:[`item.multi2`]="{ item }">{{ item.multi2 | percentFormat }}</template>
              <template v-slot:[`item.multi3a`]="{ item }">{{ item.multi3a | percentFormat }}</template>
              <template v-slot:[`item.advanced1`]="{ item }">{{ item.advanced1 | percentFormat }}</template>
              <template v-slot:[`item.advanced2`]="{ item }">{{ item.advanced2 | percentFormat }}</template>
              <template v-slot:[`item.advanced3a`]="{ item }">{{ item.advanced3a | percentFormat }}</template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
    <v-dialog v-model="dialog" max-width="450" persistent v-else>
      <v-card v-if="predictions">
        <v-card-title>Select predictions&hellip;</v-card-title>
        <v-card-text>
          <v-autocomplete 
            :items="listPredictions" 
            label="Normal prediction" 
            v-model="normal" clearable
          >
            <template v-slot:item="{ item }">
              <v-icon class="mr-4">mdi-chart-gantt</v-icon>{{ item.text }}
            </template>
          </v-autocomplete>
          <v-autocomplete 
            :items="listPredictions" 
            label="Holdback prediction" 
            v-model="holdback" 
            hint="No matching holdback found!" 
            :persistent-hint="(!!normal && !holdback)" 
            readonly 
            disabled 
          >
            <template v-slot:item="{ item }">
              <v-icon class="mr-4">mdi-chart-gantt</v-icon>{{ item.text }}
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="$router.go(-1)">Cancel</v-btn>
          <v-btn text color="success" :disabled="!normal || !holdback" @click="dialog=false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import VuePlotly from '@statnett/vue-plotly'
import AxButton from "@/components/button"
import AxToggle from "@/components/toggle"
import AxMenu from "@/components/menu"
import PrintAnalysis from "@/components/Apps/Holdback/PrintAnalysis"
import dlgRenameGroup from "@/components/Apps/Forecast/Dialogs/RenameGroup"
import Predictions from "@/api/predictions"
import Holdback from "@/api/holdback"
import Toolbar from '@/components/toolbar'
export default {
  name: "Holdback",
  props: ["application"],
  components: {
    AxButton,
    AxToggle,
    AxMenu,
    PrintAnalysis,
    dlgRenameGroup,
    Toolbar,
    VuePlotly,
  },
  data() {
    return {
      dialog: true,
      normal: null,
      holdback: null,
      predictions: [],
      settings: {},
      map: {},
      filters: {},
      wells: [],
      result: [],
      selectedWell: null,
      quality: {
        selected: "all",
        options: [
          { text: "All wells", value: "all" },
          { text: "Predicted", value: "predicted" },
          { text: "Unresolved", value: "unresolved" },
        ]
      },
      operator: {
        selected: "&",
        options: [
          { text: "Match all conditions", value: "&" },
          { text: "Match any condition", value: "|" },
        ]
      },
      colors: {
        oil: "#616161",
        gas: "#4CAF50",
        water: "#03A9F4",
        arps1: "#F44336",
        advanced3a: "#2196F3",
        advanced5: "#2196F3",
        multi3a: "#FF9800",
        multi5: "#FF9800",
        hyper5: "#9C27B0",
        hyper5a: "#1B5E20",
      },
      charts: {
        map: {
          loaded: false,
          data: [],
          layout: {
            autosize: true,
            hovermode: "closest",
            showlegend: false,
            height: 400,
            mapbox: {
              style: null,
              bearing: 0,
              center: {
                lat: 0,
                lon: 0
              },
              pitch: 0,
              zoom: 8
            },
            font: {
              family: "Roboto, sans-serif",
              color: "#808080"
            },
            margin: { l: 0, r: 0, b: 0, t: 0, pad: 0 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)"
          },
          options: {
            mapboxAccessToken: process.env.VUE_APP_MAPBOX_TOKEN,
            displaylogo: false, 
            scrollZoom: false,
            staticPlot: false, 
            responsive: true
          }
        },
        timeline: {
          data: [],
          layout: {
            autosize: true,
            barmode: "relative",
            showlegend: true,
            height: 300,
            legend: { x: 1, y: 1, xanchor: "right", bgcolor: "#FFF" },
            font: { family: "Roboto, sans-serif", color: "#808080" },
            hovermode: "closest",
            margin: { l: 60, t: 0, r: 15, b: 45 },
            xaxis: { title: { text: `Time (months)` }, dtick: 12 },
            yaxis: { title: { text: `Production` }, type: "linear" },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)"
          },
          options: { 
            displaylogo: false, 
            responsive: true 
          }
        }
      },
      groups: {
        list: [],
        selected: undefined,
        counter: 0,
      },
      targets: {
        selected: 'oil',
        options: [],
      },
      methods: [],
      timeline: null,
      session: null,
      metrics: {
        selected: 'median',
        options: [
          // { text: 'Sum', value: 'sum' },
          { text: 'Median', value: 'median' },
          { text: 'Mean', value: 'mean' },
        ]
      },
      aggregates: null,
      statistics: {
        items: [],
        headers: [],
        selected: [],
      },
      baseline: {
        options: [],
        selected: null
      },
      individuals: {
        items: [],
        headers: [],
        selected: [],
        search : ''
      },
    }
  },
  computed: {
    hasInputs() {
      return !!this.normal && !!this.holdback && !!this.settings
    },
    listPredictions() {
      return this.predictions.map(p => { 
        return { text: p.name, value: p.uuid } 
      })
    },
    hasWells() {
      return this.hasInputs && !this.isEmpty(this.wells)
    },
    prediction() {
      return this.predictions.find(p => p.uuid == this.holdback)
    },
    tWells() {
      if (this.settings && this.settings['api'] in this.map) {
        return this.map[this.settings['api']].length
      }
      return '0'
    },
    nWells() {
      if (!this.isEmpty(this.individuals.items)) {
        return this.individuals.items.length
      }
      return '0'
    },
    filteredObjects() {
      if (!this.isEmpty(this.filters)) {
        return Object.fromEntries(
          Object.entries(this.filters).filter(([k, v]) => ["object", "datetime64[ns]"].includes(v.type))
        )
      }
      return {}
    },
    filteredNumbers() {
      if (!this.isEmpty(this.filters)) {
        return Object.fromEntries(
          Object.entries(this.filters).filter(([k, v]) => ["int8", "float8", "int16", "float16", "int32", "float32", "int64", "float64"].includes(v.type))
        )
      }
      return {}
    },
    selectedGroup() {
      if (!!this.groups.selected) {
        return this.groups.list.find(g => g.id == this.groups.selected)
      }
      return undefined
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.map = {}
        this.normal = null
        this.holdback = null
        this.settings = {}
        this.production = null
        this.individuals.items = []
        this.statistics.items = []
        this.timeline = null
        this.charts.map.data = []
        this.charts.timeline.data = []
      }
    },
    normal(val) {
      this.holdback = null
      if (val) {
        let normal = this.listPredictions.find(p => p.value == this.normal)
        let holdback = this.listPredictions.filter(p => p.text == `${normal.text}_HB`)
        if (holdback.length == 1) {
          this.holdback = holdback[0].value
          this.setParameters()
          this.fetchCoordinates()
          this.fetchFilters()
          this.fetchSessions()
          this.fetchResults()
        }
      }
    },
    methods(val) {
      if (!this.isEmpty(val)) {
        this.baseline.options = [{ text: 'Production', value: 'production' }].concat(this.methods)
        this.baseline.selected = 'production'
      }
    },
    "groups.selected"(val) {
      if (!!val) {
        this.setFilters()
      } else {
        this.wells = []
        this.clearFilters()
      }
      this.fetchResults()
    },
    selectedWell(val) {
      if (val) {
        this.wells = [val]
        this.selectPointsOnMap(this.wells)
      } else {
        this.wells = []
        this.selectPointsOnMap(this.result)
      }
      this.fetchTimeline()
    }
  },
  mounted() {
    this.fetchPredictions()
  },
  methods: {
    payload() {
      return {
        normal: this.normal, 
        holdback: this.holdback, 
        wells: this.finalWells(),
        filters: !!this.selectedGroup ? this.selectedGroup.filters : this.finalFilters(),
        settings: !!this.selectedGroup ? this.selectedGroup.settings : this.requestSettings(),
      }
    },
    finalWells() {
      if (!!this.selectedWell) return [this.selectedWell]
      if (!!this.selectedGroup) return this.selectedGroup.wells
      return this.wells
    },
    finalFilters() {
      let years = {}
      let objects = {}
      let numbers = {}

      let o = Object.fromEntries(Object.entries(this.filteredObjects).filter(([k, v]) => v.selected.length > 0))
      for (const [k, v] of Object.entries(o)) {
        if (v.type == "datetime64[ns]") years[k] = v.selected
        else objects[k] = v.selected
      }

      let n = Object.fromEntries(
        Object.entries(this.filteredNumbers).filter(([k, v]) => v.selected[0] > v.min || v.selected[1] < v.max)
      )
      for (const [k, v] of Object.entries(n)) {
        numbers[k] = v.selected
      }

      let final = {}
      if (!this.isEmpty(years)) final["years"] = years
      if (!this.isEmpty(objects)) final["objects"] = objects
      if (!this.isEmpty(numbers)) final["numbers"] = numbers
      return final
    },
    requestSettings() {
      let settings = {}
      settings["metric"] = this.metrics.selected
      settings["target"] = this.targets.selected
      settings["baseline"] = this.baseline.selected
      settings["operator"] = this.operator.selected

      return settings
    },
    fetchPredictions() {
      Predictions.list('{*}').then(response => {
        this.predictions = response.data
      })
    },
    setParameters() {
      this.settings = this.prediction.config
      this.targets.options = Object.keys(this.settings['targets']).map(t => { return { text: this.capitalize(t), value: t, align: 'start' } } )
      this.targets.selected = this.targets.options[0].value
      this.methods = this.prediction.methods.map(m => { return { text: m.name, value: m.slug, align: 'end' } } )
    },
    fetchCoordinates() {
      Holdback.get_coordinates(this.holdback).then(response => {
        this.map = response.data
        this.map[this.settings.api] = this.map[this.settings.api].map(String)
        this.plotMap()
      })
    },
    fetchFilters() {
      Holdback.get_filters(this.holdback).then(response => {
        this.filters = response.data
        for (let [filter, value] of Object.entries(this.filters)) {
          if (["object", "datetime64[ns]"].includes(value.type)) this.filters[filter].selected = []
          else this.filters[filter].selected = [value.min, value.max]
        }
      })
    },
    fetchSessions() {
      Sessions.list({ user: this.user.id, application__slug: this.application.slug, prediction__uuid: this.normal }).then(response => {
        if (response.data.length > 0) {
          this.session = response.data[0]
          if ('groups' in this.session.session) {
            this.groups = JSON.parse(JSON.stringify(this.session.session.groups))
            this.$set(this.groups, 'selected', undefined)
          }
        }
      })
    },
    fetchWells() {
      Holdback.get_well_list(this.payload()).then(response => {
        this.result = response.data
        this.selectPointsOnMap(this.result)
        if (!!this.selectedGroup) {
          this.selectedGroup.count = this.result.length
        }
      })
    },
    fetchResults() {
      // if (!!this.selectedGroup) {
      //   this.selectedGroup.filters = this.finalFilters()
      //   this.selectedGroup.settings = this.requestSettings()
      // }
      this.fetchWells()
      this.fetchAggregates()
      this.fetchTimeline()
      this.fetchIndividuals()
    },
    fetchTimeline() {
      Holdback.get_timeline(this.payload()).then(response => {
        this.timeline = response.data
        this.plotTimeline()
      })
    },
    fetchAggregates() {
      Holdback.get_aggregates(this.payload()).then(response => {
        this.aggregates = response.data
        this.plotStatistics()
      })
    },
    fetchIndividuals() {
      Holdback.get_individuals(this.payload()).then(response => {
        this.individuals.items = response.data
        this.individuals.headers = [{ text: 'API', value: 'api', align: 'start' }]
        if (this.isSuper) {
          this.individuals.headers = this.individuals.headers.concat(this.methods)
        }
        this.individuals.selected = JSON.parse(JSON.stringify(this.individuals.items))
      })
    },
    fetchExports() {
      Holdback.get_exports(this.payload()).then(response => {
        let csv = response.data
        var downloadLink = document.createElement("a")
        var blob = new Blob(["\ufeff", csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `Holdback_${this.normal}_${this.holdback}.csv` //Name the file here
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      })
    },
    plotMap() {
      // Populate map
      let obj = {
        type: "scattermapbox",
        text: this.map[this.settings.api],
        lat: this.map[this.settings.latitude],
        lon: this.map[this.settings.longitude],
        mode: "markers",
        // hoverinfo: 'none',
        marker: {
          size: 6,
          reversescale: true,
          autocolorscale: false,
          colorscale: "Portland",
        },
        // selected: {
        //   marker: {
        //     size: 12,
        //     color: '#F44336',
        //   }
        // }
      }
      this.charts.map.data.push(obj)
      // Set map style
      this.charts.map.layout.mapbox.style = this.isDark ? 'mapbox://styles/schajee/cla6nsgx3002315o16d3x006u' : 'mapbox://styles/schajee/cla6np60a001z14mjo36g7dv5'

      const lats = this.map[this.settings.latitude]
      const lons = this.map[this.settings.longitude]
      // Center align map
      this.charts.map.layout.mapbox.center.lat = this.mean(lats)
      this.charts.map.layout.mapbox.center.lon = this.mean(lons)
      // Zoom map
      this.charts.map.layout.mapbox.zoom  = this.mapZoom(lats, lons)
    },
    mapZoom(lats, lons) {
      const x1 = Math.max(...lats)
      const x2 = Math.min(...lats)
      
      const y1 = Math.max(...lons)
      const y2 = Math.min(...lons)

      const max_bound = Math.max(Math.abs(x1-x2), Math.abs(y1-y2)) * 111
      const zoom = 12.5 - Math.log(max_bound)
      return zoom
    },
    plotTimeline() {
      this.charts.timeline.data = []

      // Plot actual production and declines
      this.charts.timeline.data.push({
        name: 'Production',
        type: "bar",
        showlegend: true,
        x: this.create_array(1, this.timeline['production'].length),
        y: this.timeline['production'],
        marker: { color: this.colors[this.targets.selected], opacity: 0.5 }
      })
      
      this.charts.timeline.data.push({
        name: 'Decline',
        type: "scatter",
        mode: 'lines',
        showlegend: true,
        x: this.create_array(1, this.timeline['production'].length + 12),
        y: this.timeline['decline'],
        line: { color: '#F4433680', dash: 'solid', width: 1, opacity: 0.5 }
      })

      // Plot holdback production
      this.charts.timeline.data.push({
        name: this.targets.selected,
        type: "bar",
        showlegend: false,
        x: this.create_array(this.timeline['production'].length + 1, this.timeline['production'].length + 12),
        y: this.timeline['holdback'],
        marker: { color: this.colors[this.targets.selected], opacity: 0.25 }
      })

      // Plot predictions
      this.methods.forEach(method => {
        this.charts.timeline.data.push({
          name: method.text,
          type: "scatter",
          mode: 'lines',
          showlegend: true,
          x: this.create_array(this.timeline['production'].length + 1, this.timeline['production'].length + 12),
          y: this.timeline[`${this.targets.selected}_${method.value}`],
          line: { color: this.colors[method.value], dash: 'dot', width: 1 }
        })
      })
    },
    plotStatistics() {
      this.statistics.items = []
      this.statistics.headers = [{ text: 'Target', value: 'target' }].concat(this.methods)
      this.targets.options.forEach(target => {
        let row = { 'target': target.text }
        this.methods.forEach(method => {
          let baseline = this.baseline.selected == 'production' ? target.value : `${target.value}_${this.baseline.selected}`
          row[method.value] = this.mape(this.aggregates[baseline], this.aggregates[`${target.value}_${method.value}`], this.metrics.selected)
        })
        this.statistics.items.push(row)
      })
      this.statistics.selected = this.statistics.items.filter(x => x.target == this.capitalize(this.targets.selected))
    },
    selectPointsOnMap(wells=undefined) {
      if (!this.isEmpty(this.charts.map.data)) {
        if ('selectedpoints' in this.charts.map.data[0]) {
          this.$delete(this.charts.map.data[0], "selectedpoints")
        }
        if (!!wells) {
          if (wells.length < this.tWells) {
            let indexes = this.charts.map.data[0].text.map((e, i) => (wells.includes(e) ? i : "")).filter(String)
            this.$set(this.charts.map.data[0], "selectedpoints", indexes)
          }
        }
      }
    },
    onMapClick(event) {
      if (!!event) {
        let well = event.points[0].text
        if (this.selectedWell == well) {
          this.selectedWell = null
        } else {
          this.selectedWell = well
        }
      }
    },
    onMapSelect(event) {
      if (!!event) {
        if (!this.isEmpty(event.points)) {
          this.wells = this.charts.map.data[0].text.filter((_, i) => this.charts.map.data[0].selectedpoints.some(j => i === j))
          this.fetchResults()
        }
      }
    },
    onMapClear() {
      this.wells = []
      this.fetchResults()
    },
    onExport() {
      this.fetchExports()
    },
    onMenuChange(value, variable) {
      this.setPath(this, variable, value)
    },
    onCreateGroup() {
      this.groups.counter += 1
      this.groups.list.push({
        id: this.groups.counter,
        name: `Group ${this.groups.counter}`,
        settings: this.requestSettings(),
        filters: this.finalFilters(),
        wells: this.wells,
        count: this.result.length
      })
      this.groups.selected = this.groups.counter
    },
    onCopyGroup() {
      this.groups.counter += 1
      this.groups.list.push({
        id: this.groups.counter,
        name: `Group ${this.groups.counter}`,
        settings: this.selectedGroup.settings,
        filters: this.selectedGroup.filters,
        wells: this.selectedGroup.wells,
        count: this.result.length
      })
      this.groups.selected = this.groups.counter
    },
    onRenameGroup(name) {
      if (!!this.selectedGroup) {
        this.selectedGroup.name = name
      }
    },
    onDeleteGroup(id) {
      this.groups.list = this.groups.list.filter(group => group.id !== id)
      this.groups.selected = undefined
    },
    setFilters() {
      if (!!this.selectedGroup) {
        this.clearFilters()
        this.targets.selected = this.selectedGroup.settings.target
        // this.quality.selected = this.selectedGroup.payload.quality
        this.metrics.selected = this.selectedGroup.settings.metric
        this.baseline.selected = this.selectedGroup.settings.baseline
        this.operator.selected = this.selectedGroup.settings.operator

        if ('filters' in this.selectedGroup) {
          if ("objects" in this.selectedGroup.filters) {
            Object.entries(this.selectedGroup.filters.objects).forEach(([k, v]) => {
              this.filters[k].selected = v
            })
          }
          if ("numbers" in this.selectedGroup.filters) {
            Object.entries(this.selectedGroup.filters.numbers).forEach(([k, v]) => {
              this.filters[k].selected = v
            })
          }
          if ("years" in this.selectedGroup.filters) {
            Object.entries(this.selectedGroup.filters.years).forEach(([k, v]) => {
              this.filters[k].selected = v
            })
          }
        }
      }
    },
    clearFilters() {
      Object.entries(this.filteredObjects).forEach(([k, v]) => {
        v.selected = []
      })
      Object.entries(this.filteredNumbers).forEach(([k, v]) => {
        v.selected = [v.min, v.max]
      })
      this.targets.selected = this.targets.options[0].value
      // this.quality.selected = this.quality.options[0].value
      this.metrics.selected = this.metrics.options[0].value
      this.baseline.selected = this.baseline.options[0].value
      this.operator.selected = this.operator.options[0].value
    },
    onSaveSession() {
      let payload = {
        user: this.user.id,
        application: this.application.id,
        prediction: this.predictions.find(p => p.uuid == this.normal).id,
        session: { groups: this.groups }
      }
      if (this.session) {
        Sessions.update(this.session.id, payload).then(() => this.notify('Session saved'))
      } else {
        Sessions.create(payload).then(() => this.notify('Session saved'))
      }
    },
    onRowClick(item) {
      let well = item['api']
      if (this.selectedWell == well) {
        this.selectedWell = null
      } else {
        this.selectedWell = well
      }
    },
    onRemoveWell() {
      this.selectedGroup.wells = this.individuals.selected.map(w => w['api']).slice()
      this.fetchResults()
    },
    onResetFilters() {
      if (!!this.selectedGroup) {
        this.groups.selected = undefined
      } else {
        this.wells = []
        this.clearFilters()
        this.fetchResults()
      }
    }
  }
}
</script>